import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import {
  ESeverity,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { StripeService } from "../../services/stripe/stripe.service";
import { IDeveloperBillingRestClient } from "../../rest-clients/billing/i-developer-billing-rest.client";
import { developerBillingRestClientInjectionToken } from "../../rest-clients/billing/developer-billing-rest-client-injection-token";
import type { Stripe, StripeElements, StripePaymentElement } from "@stripe/stripe-js";

interface IAddPaymentMethodModalData {
  onSaveCallback: () => void;
}

@Component({
  selector: "app-add-payment-method-modal",
  templateUrl: "./add-payment-method-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule
  ]
})
export class AddPaymentMethodModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild("paymentElement") paymentElementRef!: ElementRef<HTMLDivElement>;

  public isLoading: boolean = true;
  public isSubmitting: boolean = false;
  public errorMessage: string | null = null;

  private _stripe: Stripe | null = null;
  private _elements: StripeElements | null = null;
  private _paymentElement: StripePaymentElement | null = null;
  private readonly _data: IAddPaymentMethodModalData;

  constructor(
    private readonly _stripeService: StripeService,
    @Inject(developerBillingRestClientInjectionToken) private readonly _billingRestClient: IDeveloperBillingRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    private readonly _dynamicDialogRef: DynamicDialogRef,
    private readonly _dynamicDialogConfig: DynamicDialogConfig,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this._data = this._dynamicDialogConfig.data;
  }

  async ngAfterViewInit(): Promise<void> {
    this._stripe = await this._stripeService.getStripe();
    if (!this._stripe) {
      this.errorMessage = "Stripe konnte nicht geladen werden";
      this.isLoading = false;
      this._cdr.markForCheck();
      return;
    }

    try {
      const setupIntent = await this._billingRestClient.createSetupIntent();
      const isDark = document.documentElement.classList.contains("app-dark");
      const computedStyle = getComputedStyle(document.documentElement);
      const primaryColor = computedStyle.getPropertyValue("--color-zaeper-primary-500").trim() || (isDark ? "#a5b4fc" : "#6366f1");
      const primaryColorLight = computedStyle.getPropertyValue("--color-zaeper-primary-400").trim() || "#a5b4fc";

      this._elements = this._stripe.elements({
        clientSecret: setupIntent.clientSecret,
        appearance: {
          theme: isDark ? "night" : "flat",
          variables: {
            fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
            fontSizeBase: "14px",
            fontWeightNormal: "400",
            fontWeightMedium: "500",
            borderRadius: "8px",
            colorPrimary: isDark ? primaryColorLight : primaryColor,
            colorBackground: isDark ? "#1e293b" : "#ffffff",
            colorText: isDark ? "#e2e8f0" : "#334155",
            colorTextSecondary: isDark ? "#94a3b8" : "#64748b",
            colorDanger: "#ef4444",
            spacingUnit: "4px",
            spacingGridRow: "16px",
            spacingGridColumn: "16px"
          },
          rules: {
            ".Input": {
              border: isDark ? "1px solid #334155" : "1px solid #e2e8f0",
              boxShadow: "none",
              padding: "10px 12px"
            },
            ".Input:focus": {
              border: `1px solid ${primaryColor}`,
              boxShadow: `0 0 0 1px ${primaryColor}`
            },
            ".Label": {
              fontWeight: "500",
              marginBottom: "6px"
            },
            ".Tab": {
              border: isDark ? "1px solid #334155" : "1px solid #e2e8f0",
              borderRadius: "8px",
              justifyContent: "center"
            },
            ".Tab:hover": {
              backgroundColor: isDark ? "#334155" : "#f1f5f9"
            },
            ".Tab--selected": {
              borderColor: primaryColor,
              backgroundColor: isDark ? "#1e1b4b" : "#eef2ff",
              color: isDark ? primaryColorLight : primaryColor
            },
            ".Tab--selected:hover": {
              backgroundColor: isDark ? "#1e1b4b" : "#eef2ff",
              color: isDark ? primaryColorLight : primaryColor
            },
            ".Tab--selected .TabIcon": {
              fill: isDark ? primaryColorLight : primaryColor
            },
            ".Tab--selected .TabLabel": {
              color: isDark ? primaryColorLight : primaryColor
            },
            ".TabIcon--selected": {
              fill: isDark ? primaryColorLight : primaryColor
            },
            ".Badge": {
              display: "none"
            },
            ".Link": {
              color: isDark ? primaryColorLight : primaryColor
            },
            ".Link .LinkIcon": {
              fill: isDark ? primaryColorLight : primaryColor
            }
          }
        }
      });

      this._paymentElement = this._elements.create("payment", {
        layout: "tabs"
      });

      this._paymentElement.mount(this.paymentElementRef.nativeElement);
      this.isLoading = false;
      this._cdr.markForCheck();
    } catch {
      this.errorMessage = "Zahlungsformular konnte nicht geladen werden";
      this.isLoading = false;
      this._cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this._paymentElement?.destroy();
  }

  public async submit(): Promise<void> {
    if (!this._stripe || !this._elements || this.isSubmitting) return;

    this.isSubmitting = true;
    this.errorMessage = null;
    this._cdr.markForCheck();

    const { error } = await this._stripe.confirmSetup({
      elements: this._elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: "if_required"
    });

    if (error) {
      this.errorMessage = error.message ?? "Ein Fehler ist aufgetreten";
      this.isSubmitting = false;
      this._cdr.markForCheck();
    } else {
      this._toastService.setMessage({
        title: "Erfolgreich",
        detail: "Zahlungsmethode wurde hinzugefügt",
        severity: ESeverity.success
      });
      this._data.onSaveCallback();
    }
  }

  public close(): void {
    this._dynamicDialogRef.close();
  }
}
